/**
 * Daily Log and Absence Note mutations for timeline service.
 *
 * Extracted from timeline.ts to reduce file size.
 *
 * @module fehlzeiten/services/timelineDailyLogMutations
 */

import { supabase } from "../../../../lib/supabase";
import type { ServiceResult } from "../types/services";

// ============================================================================
// Types
// ============================================================================

/**
 * Parameters for syncing daily log status.
 */
export interface SyncDailyLogParams {
  studentId: string;
  date: string;
  presenceStatus: string;
}

// ============================================================================
// Daily Log Mutations
// ============================================================================

/**
 * Syncs the daily log presence status based on lesson attendance.
 *
 * @param params - Sync parameters
 * @returns Service result
 */
export async function syncDailyLogStatus(
  params: SyncDailyLogParams,
): Promise<ServiceResult> {
  const { studentId, date, presenceStatus } = params;

  const { error } = await supabase
    .from("student_daily_log" as never)
    .update({
      presence_status: presenceStatus,
      updated_at: new Date().toISOString(),
    } as never)
    .eq("student_id" as never, studentId as never)
    .eq("date" as never, date as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Reverse (clear) a student's check-in for a given day.
 *
 * Used when a kid was checked in by mistake — most commonly via a
 * class-wide "Alle anwesend" bulk action that also stamped kids who
 * never actually showed up. Clears `check_in_time` / `check_in_by` /
 * `check_in_method`; `presence_status` is intentionally left untouched
 * (a kid mid-Klärung stays `absent_unclear` — removing a bogus check-in
 * shouldn't silently flip their day status).
 *
 * Goes through RLS like syncDailyLogStatus — a class teacher can update
 * their own students' daily_log rows via `teacher_can_update_daily_log`.
 * `lastUpdatedBy` (the acting staff's user_profiles id) is stamped for a
 * minimal audit trail.
 */
export async function reverseCheckIn(params: {
  studentId: string;
  date: string;
  lastUpdatedBy?: string | null;
}): Promise<ServiceResult> {
  const { studentId, date, lastUpdatedBy } = params;

  const { error } = await supabase
    .from("student_daily_log" as never)
    .update({
      check_in_time: null,
      check_in_by: null,
      check_in_method: null,
      ...(lastUpdatedBy ? { last_updated_by: lastUpdatedBy } : {}),
      updated_at: new Date().toISOString(),
    } as never)
    .eq("student_id" as never, studentId as never)
    .eq("date" as never, date as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Updates trip attendance in student_attendance_logs via the ad-hoc trip lesson.
 *
 * Finds the ad-hoc course_lesson linked to the class_absence and updates
 * the student's attendance_log status. Falls back to daily_log for legacy data.
 *
 * @param studentId - Student UUID
 * @param date - Date in YYYY-MM-DD format
 * @param presenceStatus - The status to set (present, late, absent_excused, absent_unexcused)
 * @param classAbsenceId - Optional class absence ID to locate the trip lesson
 * @returns Service result
 */
export async function updateTripAttendance(
  studentId: string,
  date: string,
  presenceStatus: string,
  classAbsenceId?: string | null,
): Promise<ServiceResult> {
  // Strategy: find the ad-hoc trip lesson for this class_absence + date,
  // then update the student_attendance_logs entry.
  if (classAbsenceId) {
    // Find the trip lesson
    const { data: tripLessons } = await supabase
      .from("course_lessons" as never)
      .select("id, school_id" as never)
      .eq("class_absence_id" as never, classAbsenceId as never)
      .gte("start_datetime" as never, `${date}T00:00:00` as never)
      .lt("start_datetime" as never, `${date}T23:59:59` as never)
      .limit(1);

    const tripLesson = (tripLessons as any)?.[0];
    if (tripLesson?.id) {
      // Update existing attendance log
      const { data: existing } = await supabase
        .from("student_attendance_logs" as never)
        .select("id" as never)
        .eq("student_id" as never, studentId as never)
        .eq("lesson_id" as never, tripLesson.id as never)
        .limit(1);

      if ((existing as any)?.length > 0) {
        const { error } = await supabase
          .from("student_attendance_logs" as never)
          .update({ status: presenceStatus } as never)
          .eq("student_id" as never, studentId as never)
          .eq("lesson_id" as never, tripLesson.id as never);

        if (error) {
          return { success: false, error: error.message };
        }
        return { success: true };
      } else {
        // Create new attendance log if it doesn't exist
        const { error } = await supabase
          .from("student_attendance_logs" as never)
          .insert({
            lesson_id: tripLesson.id,
            student_id: studentId,
            status: presenceStatus,
            school_id: tripLesson.school_id,
          } as never);

        if (error) {
          return { success: false, error: error.message };
        }
        return { success: true };
      }
    }
  }

  // Fallback for legacy data without ad-hoc lessons
  const noteParts: string[] = [];
  if (classAbsenceId) {
    noteParts.push(`class_absence:${classAbsenceId}`);
  }
  noteParts.push("method:class_trip");
  const notes = noteParts.join(";");

  const { error } = await supabase
    .from("student_daily_log" as never)
    .update({
      presence_status: presenceStatus,
      notes: notes,
      updated_at: new Date().toISOString(),
    } as never)
    .eq("student_id" as never, studentId as never)
    .eq("date" as never, date as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Clears the absence note link from a daily log.
 *
 * @param studentId - Student UUID
 * @param date - Date in YYYY-MM-DD format
 * @returns Service result
 */
export async function clearDailyLogAbsenceLink(
  studentId: string,
  date: string,
): Promise<ServiceResult> {
  const { error } = await supabase
    .from("student_daily_log" as never)
    .update({ absence_note_id: null } as never)
    .eq("student_id" as never, studentId as never)
    .eq("date" as never, date as never);

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

// ============================================================================
// Absence Note Mutations
// ============================================================================

/**
 * Deletes a single-day absence note for a student on a specific date.
 *
 * Only deletes notes where start_date == end_date == date to prevent
 * accidentally deleting multi-day notes.
 *
 * @param studentId - Student UUID
 * @param date - Date in YYYY-MM-DD format
 * @returns Service result
 */
export async function deleteSingleDayAbsenceNote(
  studentId: string,
  date: string,
): Promise<ServiceResult> {
  const { error } = await supabase
    .from("student_absence_notes" as never)
    .delete()
    .eq("student_id" as never, studentId as never)
    .eq("start_date" as never, date as never)
    .eq("end_date" as never, date as never);

  if (error) {
    return { success: false, error: error.message };
  }

  // Also clear the daily log link
  await clearDailyLogAbsenceLink(studentId, date);

  return { success: true };
}

/**
 * Cleans up orphaned absence notes when all lessons are marked present.
 *
 * Uses the `fn_cleanup_orphaned_absence_notes` RPC to remove notes
 * that no longer have any absent lessons associated.
 *
 * @param studentId - Student UUID
 * @param date - Date in YYYY-MM-DD format
 * @returns Service result
 */
export async function cleanupOrphanedAbsenceNotes(
  studentId: string,
  date: string,
): Promise<ServiceResult> {
  const { error } = await supabase.rpc(
    "fn_cleanup_orphaned_absence_notes" as never,
    {
      p_student_id: studentId,
      p_date: date,
    } as never,
  );

  if (error) {
    console.warn("[timeline] Failed to cleanup orphaned absence notes:", error);
    return { success: false, error: error.message };
  }

  return { success: true };
}
